import { Client, getStateCallbacks, type Room } from "@colyseus/sdk";
import type { BlockOverrideEntry } from "@craftgaming/shared";

export type RemotePlayerState = {
  sessionId: string;
  username: string;
  /** ChampionClass id — drives the outfit color everyone sees. */
  championClass: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
};

export type ChatBroadcast = {
  playerId: string;
  username: string;
  text: string;
  system?: boolean;
};

export type NetworkCallbacks = {
  onPlayerAdd: (player: RemotePlayerState) => void;
  onPlayerUpdate: (player: RemotePlayerState) => void;
  onPlayerRemove: (sessionId: string) => void;
  onBlockUpdate: (wx: number, wy: number, wz: number, blockId: number) => void;
  onOverrides: (list: BlockOverrideEntry[]) => void;
  onChat: (message: ChatBroadcast) => void;
  /** Another player hit us (PvP) — apply the damage locally. */
  onPlayerHit: (from: string, damage: number) => void;
  /** Fired once when the connection is lost (not on intentional disconnect). */
  onDisconnect: (code: number) => void;
};

export type NetworkSession = {
  readonly sessionId: string;
  readonly seed: number;
  /** Wire game handlers. Messages received before this call are buffered and flushed here. */
  bindCallbacks: (cb: NetworkCallbacks) => void;
  isConnected: () => boolean;
  /** Server-synced time of day (0-1, see day-night.ts). */
  getTimeOfDay: () => number;
  sendInput: (input: { x: number; y: number; z: number; yaw: number; pitch: number }) => void;
  sendBlockBreak: (wx: number, wy: number, wz: number) => void;
  sendBlockPlace: (wx: number, wy: number, wz: number, blockId: number) => void;
  sendChat: (text: string) => void;
  /** PvP: report that we hit another player for `damage`. */
  sendHit: (target: string, damage: number) => void;
  disconnect: () => void;
};

// Colyseus schema instances are typed on the server package; on the client
// we only read these fields off the synced state.
type PlayerSchemaView = {
  username: string;
  championClass: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
};

type GameStateView = {
  players: Map<string, PlayerSchemaView>;
  worldSeed: number;
  timeOfDay: number;
  tick: number;
};

function toRemoteState(sessionId: string, p: PlayerSchemaView): RemotePlayerState {
  return {
    sessionId,
    username: p.username,
    championClass: p.championClass,
    x: p.x,
    y: p.y,
    z: p.z,
    yaw: p.yaw,
    pitch: p.pitch,
  };
}

/**
 * Connects to the Colyseus GameRoom and waits for the initial state
 * (which carries the authoritative world seed). Rejects on timeout so
 * the caller can fall back to single-player.
 */
export async function connectToServer(
  url: string,
  username: string,
  championClass: string,
  timeoutMs = 5000
): Promise<NetworkSession> {
  const client = new Client(url);

  const joinPromise = client.joinOrCreate<GameStateView>("game", { username, championClass });
  let room: Room<any, GameStateView>;
  try {
    room = await withTimeout(joinPromise, timeoutMs, `Timeout connecting to ${url}`);
  } catch (err) {
    // If the join resolves after the timeout, close the late room —
    // otherwise a ghost player sits in the server forever.
    joinPromise.then((lateRoom) => lateRoom.leave()).catch(() => {});
    throw err;
  }

  // The seed arrives with the first state patch; wait for it before
  // generating any terrain.
  try {
    await withTimeout(
      new Promise<void>((resolve) => {
        if (room.state.worldSeed !== undefined && room.state.players !== undefined) {
          resolve();
          return;
        }
        room.onStateChange.once(() => resolve());
      }),
      timeoutMs,
      "Timeout waiting for initial game state"
    );
  } catch (err) {
    room.leave().catch(() => {});
    throw err;
  }

  // Buffer messages that arrive before the game binds its handlers
  // (the server sends world:overrides immediately on join).
  let callbacks: NetworkCallbacks | null = null;
  let pendingOverrides: BlockOverrideEntry[] = [];
  const pendingBlockUpdates: Array<{ wx: number; wy: number; wz: number; blockId: number }> = [];
  const pendingChat: ChatBroadcast[] = [];

  // Connection lifecycle — an unnoticed drop would leave the game silently
  // pretending to be online (frozen avatars, inputs queued into the void).
  let connected = true;
  let intentionalLeave = false;

  room.onError((code: number, message?: string) => {
    console.error(`[Network] Room error ${code}: ${message ?? ""}`);
  });

  room.onLeave((code: number) => {
    connected = false;
    if (!intentionalLeave) {
      console.warn(`[Network] Disconnected from server (code ${code})`);
      callbacks?.onDisconnect(code);
    }
  });

  room.onMessage("world:overrides", (data: { list: BlockOverrideEntry[] }) => {
    const list = Array.isArray(data?.list) ? data.list : [];
    if (callbacks) callbacks.onOverrides(list);
    else pendingOverrides = pendingOverrides.concat(list);
  });

  room.onMessage("block:update", (data: { wx: number; wy: number; wz: number; blockId: number }) => {
    if (callbacks) callbacks.onBlockUpdate(data.wx, data.wy, data.wz, data.blockId);
    else pendingBlockUpdates.push(data);
  });

  room.onMessage("chat:broadcast", (data: ChatBroadcast) => {
    if (typeof data?.text !== "string") return;
    if (callbacks) callbacks.onChat(data);
    else pendingChat.push(data);
  });

  room.onMessage("player:hit", (data: { from: string; damage: number }) => {
    if (typeof data?.damage !== "number") return;
    callbacks?.onPlayerHit(data.from ?? "", data.damage);
  });

  // Terrain is generated locally from the seed; chunk:data is unused.
  room.onMessage("chunk:data", () => {
    console.debug("[Network] chunk:data ignored (terrain generated locally)");
  });

  const $ = getStateCallbacks(room);

  function bindCallbacks(cb: NetworkCallbacks): void {
    callbacks = cb;

    // onAdd fires for players already present at bind time.
    $(room.state).players.onAdd((player: PlayerSchemaView, sessionId: string) => {
      if (sessionId === room.sessionId) return;
      cb.onPlayerAdd(toRemoteState(sessionId, player));

      const notify = () => cb.onPlayerUpdate(toRemoteState(sessionId, player));
      $(player).listen("x", notify);
      $(player).listen("y", notify);
      $(player).listen("z", notify);
      $(player).listen("yaw", notify);
      $(player).listen("pitch", notify);
    });

    $(room.state).players.onRemove((_player: PlayerSchemaView, sessionId: string) => {
      if (sessionId === room.sessionId) return;
      cb.onPlayerRemove(sessionId);
    });

    if (pendingOverrides.length > 0) {
      cb.onOverrides(pendingOverrides);
      pendingOverrides = [];
    }
    for (const update of pendingBlockUpdates) {
      cb.onBlockUpdate(update.wx, update.wy, update.wz, update.blockId);
    }
    pendingBlockUpdates.length = 0;

    for (const message of pendingChat) {
      cb.onChat(message);
    }
    pendingChat.length = 0;
  }

  return {
    sessionId: room.sessionId,
    seed: room.state.worldSeed,
    bindCallbacks,
    isConnected: () => connected,
    getTimeOfDay: () => room.state.timeOfDay ?? 0,
    sendInput(input) {
      if (connected) room.send("player:input", input);
    },
    sendBlockBreak(wx, wy, wz) {
      if (connected) room.send("block:break", { wx, wy, wz });
    },
    sendBlockPlace(wx, wy, wz, blockId) {
      if (connected) room.send("block:place", { wx, wy, wz, blockId });
    },
    sendChat(text) {
      if (connected) room.send("chat:message", { text });
    },
    sendHit(target, damage) {
      if (connected) room.send("player:hit", { target, damage });
    },
    disconnect() {
      intentionalLeave = true;
      room.leave().catch(() => {});
    },
  };
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}
